// /utils/botFactoryAPI.tsx
// CLIENT SIDE : CRUD Integration with Loopback 4 API Gateway - MongoDB
import axios from "axios";

// CRA only exposes client env vars prefixed with REACT_APP_.
// Keep legacy fallbacks to avoid breaking existing deployments.
// ⚠️ The 2nd and 3rd fallbacks were REMOVED 2026-08-14, not kept "just in case":
// `REACT_API_BASE_URL` (missing `_APP`) and bare `API_BASE_URL` are BOTH un-inlinable by CRA, so
// they could never contribute a value — they only made the chain look like it had redundancy.
// Trap-shaped dead code: the next reader sets one of them and watches nothing happen.
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  'https://robot-api-37f87b97b291.herokuapp.com/';

// Build metric URL dynamically so it works when hosted behind any domain/port
function getMetricsUrl(includeTokenInQuery = false, token?: string) {
  // Prefer explicit API base to avoid mismatched origins (e.g., app on 8081, API on 10000)
  const base = API_BASE_URL || (typeof window !== 'undefined' && window.location.origin) || '';

  const url = new URL('/botMetricsClient', base);
  if (includeTokenInQuery && token) {
    url.searchParams.set('auth', token);
  }
  return url.toString();
}

const METRICS_URL = getMetricsUrl();
// console.log('[Metrics] METRICS_URL resolved', METRICS_URL);

// Whether this browser honors fetch(..., { keepalive: true }) — required for a metrics POST to
// survive page unload/freeze (the mobile background-then-killed case). Chromium (desktop +
// Android) and Safari 16.4+ support it; older iOS Safari ignores the flag. When unsupported we
// skip the keepalive fetch entirely and fall straight to the synchronous sendBeacon path, which
// survives backgrounding on ALL iOS versions — matching the original mobile-safe behavior.
const SUPPORTS_FETCH_KEEPALIVE = (() => {
  try {
    return typeof Request !== 'undefined' && 'keepalive' in new Request('http://localhost');
  } catch {
    return false;
  }
})();

// Persistent trace for post-mortem after tab closes
const METRICS_TRACE_KEY = 'metrics_trace_log';
export function pushMetricsTrace(msg: string, data: Record<string, any> = {}) {
  // Debug trace disabled
  return;
}

function formatSeconds(totalSeconds: number) {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function normalizeMetricsPayload(metricData: any) {
  const normalized = { ...metricData };
  if (!normalized.botName) {
    try {
      const storedName = localStorage.getItem('selectedBotName');
      if (storedName) normalized.botName = storedName;
    } catch (err) {
      // ignore
    }
    if (!normalized.botName) {
      normalized.botName = null;
    }
  }

  // Normalize engagementTime to string HH:MM:SS but do not add extra fields
  if (typeof normalized.engagementTime === 'number') {
    normalized.engagementTime = formatSeconds(normalized.engagementTime);
  }

  return normalized;
}

// ---- Metric queue helpers (persist across tab closes) ----
const METRIC_QUEUE_KEY = 'cmw_pending_metrics';

type QueuedMetric = {
  id: string;
  timestamp: number;
  payload: Record<string, any>;
};

function loadMetricQueue(): QueuedMetric[] {
  try {
    const raw = localStorage.getItem(METRIC_QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedMetric[]) : [];
  } catch (err) {
    console.warn('[Metrics] Failed to load queue:', err);
    return [];
  }
}

function saveMetricQueue(queue: QueuedMetric[]) {
  try {
    localStorage.setItem(METRIC_QUEUE_KEY, JSON.stringify(queue));
  } catch (err) {
    console.warn('[Metrics] Failed to save queue:', err);
  }
}

export function clearMetricQueue() {
  try {
    localStorage.removeItem(METRIC_QUEUE_KEY);
  } catch (err) {
    console.warn('[Metrics] Failed to clear queue:', err);
  }
}

function enqueueMetric(payload: Record<string, any>) {
  // console.log('[Metrics] Enqueue metric payload');
  const queue = loadMetricQueue();
  const id = `metric_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  queue.push({
    id,
    timestamp: Date.now(),
    payload,
  });
  saveMetricQueue(queue);
  return id;
}

function removeMetricFromQueue(id: string) {
  const queue = loadMetricQueue();
  const remaining = queue.filter(entry => entry.id !== id);
  if (remaining.length !== queue.length) {
    saveMetricQueue(remaining);
  }
}

async function flushMetricQueue(token: string) {
  // console.log('[Metrics] Flush queue start');
  const queue = loadMetricQueue();
  if (!queue.length) return;

  const remaining: QueuedMetric[] = [];
  for (const entry of queue) {
    try {
      const normalized = normalizeMetricsPayload(entry.payload);
      await axios.post(METRICS_URL, normalized, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      // console.log('[Metrics] Flushed queued metric:', entry.id);
    } catch (err) {
      // console.warn('[Metrics] Failed to flush queued metric, keeping:', entry.id, err);
      remaining.push(entry);
    }
  }

  if (remaining.length !== queue.length) {
    saveMetricQueue(remaining);
  }
}

/**
 * Try to send metrics with a keepalive fetch so the request can complete after
 * the page is unloading. Falls back to axios if keepalive is not available or
 * the request fails.
 */
async function postMetricsWithKeepalive(metricData: Record<string, any>, token: string) {
  if (typeof fetch === 'undefined') return null;

  try {
    const body = JSON.stringify(metricData);

    // Keepalive fetch with header auth (non-credentialed) — survives page unload without
    // sendBeacon's quirks (no custom headers, always credentialed). We intentionally do NOT
    // try sendBeacon first here: navigator.sendBeacon() returns true the moment the browser
    // QUEUES the blob and never reports the later CORS/auth failure, which previously
    // short-circuited (returned {queued:true}) before this correct request could run.
    const response = await fetch(METRICS_URL, {
      method: 'POST',
      keepalive: true,
      mode: 'cors',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body,
    });

    if (!response.ok) {
      console.warn('⚠️ BotMetricClient keepalive POST failed, falling back:', response.status, response.statusText);
      return null;
    }

    return await response.json();
  } catch (err) {
    console.warn('⚠️ BotMetricClient keepalive POST errored, falling back:', err);
    return null;
  }
}

/**
 * Last-resort synchronous XHR for unload paths. Blocks the thread until the
 * request completes. Use only when beacon/keepalive fail.
 */
function postMetricsSync(metricData: Record<string, any>, token: string) {
  try {
    if (typeof XMLHttpRequest === 'undefined') return false;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', METRICS_URL, false); // synchronous (blocks)
    xhr.setRequestHeader('Content-Type', 'application/json');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.send(JSON.stringify(metricData));
    const ok = xhr.status >= 200 && xhr.status < 300;
    // Debug output disabled for sync JSON post
    return ok;
  } catch (err) {
    // console.warn('⚠️ BotMetricClient sync POST errored:', err);
    return false;
  }
}

/**
 * Synchronous form-encoded POST (mimics $.ajax({ async:false, data: metricData }))
 * Some backends parse urlencoded bodies more reliably during unload.
 */
function postMetricsSyncForm(metricData: Record<string, any>, token: string) {
  try {
    if (typeof XMLHttpRequest === 'undefined') return false;
    const xhr = new XMLHttpRequest();
    xhr.open('POST', METRICS_URL, false); // synchronous
    xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    const encoded = Object.entries(metricData)
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(typeof v === 'object' ? JSON.stringify(v) : v ?? '')}`)
      .join('&');
    xhr.send(encoded);
    const ok = xhr.status >= 200 && xhr.status < 300;
    // Debug output disabled for sync form post
    return ok;
  } catch (err) {
    // console.warn('⚠️ BotMetricClient sync form POST errored:', err);
    return false;
  }
}

/**
 * Upload a profile picture to the /files endpoint.
 * @param file - The image file to upload
 * @param metadata - Optional metadata to send with the file
 * @returns Promise<{id: string, ...}> - The server response, should include the uploaded pic ID
 */
export async function uploadProfilePic(file: File, metadata: Record<string, any> = {}): Promise<any> {
  const formData = new FormData();
  formData.append('file', file);
  Object.entries(metadata).forEach(([key, value]) => {
    formData.append(key, value);
  });
  try {
    // Use API_BASE_URL 
    const response = await axios.post(`${API_BASE_URL}/imageStorage/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  } catch (error: any) {
    // Try to extract server error message
    let errMsg = 'Upload failed';
    if (error.response && error.response.data) {
      if (typeof error.response.data === 'string') {
        errMsg += ': ' + error.response.data.slice(0, 100);
      } else if (error.response.data.error) {
        errMsg += ': ' + error.response.data.error;
      }
    } else if (error.message) {
      errMsg += ': ' + error.message;
    }
    throw new Error(errMsg);
  }
}

/** What the account API stores for "no picture". Sign-up has always sent it; the header shows the silhouette for it. */
export const NO_AVATAR_PIC = '/assets/avatar-icon.jpg';

/** A link to a picture on any web site: http or https. Returns the cleaned link, or throws a message for a person. */
export function parseAvatarLink(text: string): string {
  let url: URL;
  try {
    url = new URL(text.trim());
  } catch {
    throw new Error('That is not a valid link.');
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') throw new Error('The link must start with http:// or https://');
  return url.toString();
}

/**
 * The `pic` value to store for a picture: an uploaded file becomes its originFileId, a string (an image id or a
 * link) is kept as it is, and nothing gives undefined. Sign-up and the Account section both store pictures this way.
 */
export async function resolveProfilePic(pic?: File | string): Promise<string | undefined> {
  if (pic instanceof File) {
    const uploadResult = await uploadProfilePic(pic);
    if (uploadResult && uploadResult.originFileId) {
      console.log('📡 [resolveProfilePic] Uploaded pic, originFileId:', uploadResult.originFileId);
      return uploadResult.originFileId;
    }
    console.warn('⚠️ [resolveProfilePic] Upload succeeded but no originFileId returned.');
    return undefined;
  }
  if (typeof pic === 'string' && pic.length > 0) return pic;
  return undefined;
}

/**
 * Change a signed-in user's picture. `pic` is an uploaded file (already checked by the shared Upload component), a
 * link to an image on any web site (http or https), or null to remove it. Returns the `pic` value now stored. PATCH /users/{id}.
 */
export async function updateUserAvatar(userId: string, token: string, pic: File | string | null): Promise<string> {
  let value: string;
  if (pic === null) {
    value = NO_AVATAR_PIC;
  } else if (pic instanceof File) {
    const id = await resolveProfilePic(pic);
    if (!id) throw new Error('The picture could not be uploaded. Please try again.');
    value = id;
  } else {
    value = parseAvatarLink(pic);
  }
  try {
    await axios.patch(
      `${API_BASE_URL}/users/${userId}`,
      { pic: value },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, timeout: 30000 },
    );
  } catch (error: any) {
    console.error('❌ [updateUserAvatar] failed:', error.response?.data || error.message);
    const err = error.response?.data?.error;
    throw new Error((typeof err === 'string' ? err : err?.message) || error.message || 'The picture could not be saved.');
  }
  return value;
}

/**
 * Sign in a user.
 * @param {string} email - The user's email.
 * @param {string} password - The user's password.
 * @param {string} org - The user's org.
 * @param {string} role - The user's role.
 * @returns {Promise<any>} - The API response.
 */
export async function signIn(email: string, password: string): Promise<any> {
 
  try {
    const response = await axios.post(`${API_BASE_URL}/users/signin`, {
      email,
      password,
    });
    return response.data;
  } catch (error: any) {
    console.error("❌ Sign In Error raw:", error);
    if (error.response?.data) {
      console.error("❌ Sign In Error response data:", error.response.data);
      if (typeof error.response.data === "string") {
        throw new Error(error.response.data);
      } else if (error.response.data.error) {
        // error.response.data.error can be an object
        if (typeof error.response.data.error === "string") {
          throw new Error(error.response.data.error);
        } else {
          // Convert error object to string
          throw new Error(JSON.stringify(error.response.data.error));
        }
      } else {
        throw new Error(JSON.stringify(error.response.data));
      }
    }
    throw new Error(error.message || "❌ Sign In failed.");
  }
}
/**
 * Check if email already exists
 * @param email - The email to check
 * @returns Promise<boolean> - True if email exists, false otherwise
 */
export async function checkEmailExists(email: string): Promise<boolean> {
  try {
    const response = await axios.get(`${API_BASE_URL}/users/check-email`, {
      params: {
        email: email
      },
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });
    return response.data.exists; // Expecting { exists: true/false } from backend
  } catch (error) {
    console.error('Error checking email:', error);
    throw error;
  }
}


/**
 * Sign up a new user
 * @param userData - User data matching customUser model
 * @returns Promise with the API response
 */
export async function signUp(userData: {
  id?: string;
  email: string;
  password: string;
  username?: string;
  salutation?: string;
  firstname: string;
  lastname: string;
  mobile: string;
  org: object;
  role: string[];
  pic?: File | string;
  footerProfile?: {
    notes?: string;
    active?: boolean;
    createdBy?: string;
    updatedBy?: string;
    created?: string;
    updated?: string;
  };
  /** The agent the visitor signed up from (the page's ?id=). With it, the account API emails a verification link. */
  botId?: string;
}): Promise<any> {
  console.log('🔍 [signUp] Starting signup process');

  try {
    console.log('🔍 [signUp] Checking if email exists:', userData.email);
    const emailExists = await checkEmailExists(userData.email);
    if (emailExists) {
      console.log('❌ [signUp] Email already exists:', userData.email);
      throw new Error('This email is already registered. Please use a different email or sign in.');
    }

    const fileOriginId = await resolveProfilePic(userData.pic);

    // v2.08: built field by field, never `...userData`, so `emailVerified` / `verificationToken` can't ride
    // along (a client could sign itself up as verified). The account API sets both; it ignores them if sent.
    const botId = typeof userData.botId === 'string' ? userData.botId.trim() : '';
    const jsonPayload: Record<string, unknown> = {
      email: userData.email,
      password: userData.password,
      username: userData.username,
      salutation: userData.salutation,
      firstname: userData.firstname,
      lastname: userData.lastname,
      mobile: userData.mobile,
      org: userData.org,
      role: userData.role,
      pic: fileOriginId || NO_AVATAR_PIC,
    };
    if (userData.id !== undefined) jsonPayload.id = userData.id;
    if (userData.footerProfile !== undefined) jsonPayload.footerProfile = userData.footerProfile;
    // Over 200 characters the API would refuse the whole sign-up, so an odd id just means no email.
    if (botId && botId.length <= 200) jsonPayload.botId = botId;

    const response = await axios.post(`${API_BASE_URL}/users/signup`, jsonPayload, {
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 30000,
    });

    console.log(`✅ [signUp] Request succeeded`);
    console.log('📥 [signUp] Response data:', JSON.stringify(response.data, null, 2));

    return response.data;
  } catch (error: any) {
    console.error('❌ [signUp] Error response:', error.response?.data || error.message);
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        throw new Error(error.response.data);
      } else if (error.response.data.error) {
        const err = error.response.data.error;
        throw new Error(typeof err === 'string' ? err : JSON.stringify(err));
      } else {
        throw new Error(JSON.stringify(error.response.data));
      }
    }
    throw new Error(error.message || '❌ Sign Up failed.');
  }
}




/**
 * Password reset (Robot Factory API).
 *
 * ⚠️ PLACEHOLDER ENDPOINTS. The Robot Factory API does not expose password reset yet. These two
 * paths and the payloads below are a proposed contract so the sign-in "Forgot password" flow and
 * the reset page can be built and tested. When the real endpoints are exposed, change
 * PASSWORD_RESET_PATHS (and the payloads, if they differ) here and nowhere else.
 */
export const PASSWORD_RESET_PATHS = {
  request: '/users/password-reset/request', // POST { email, botId?, resetUrl }   -> 200/204
  confirm: '/users/password-reset/confirm', // POST { token, password }           -> 200/204
} as const;

/** What went wrong, in terms the reset page can word for a person. */
export type PasswordResetErrorCode = 'invalid' | 'expired' | 'unavailable' | 'other';

export class PasswordResetError extends Error {
  code: PasswordResetErrorCode;
  constructor(code: PasswordResetErrorCode, message: string) {
    super(message);
    this.name = 'PasswordResetError';
    this.code = code;
  }
}

function toPasswordResetError(error: any): PasswordResetError {
  const status: number | undefined = error?.response?.status;
  const data = error?.response?.data;
  const text = typeof data === 'string' ? data : data?.error?.message || data?.error || data?.message || '';
  const msg = typeof text === 'string' ? text : JSON.stringify(text);
  if (status === 410 || /expired/i.test(msg)) return new PasswordResetError('expired', msg || 'This reset link has expired.');
  if (status === 400 || status === 401 || status === 403 || status === 422 || /invalid/i.test(msg)) {
    return new PasswordResetError('invalid', msg || 'This reset link is not valid.');
  }
  // 404 (endpoint not exposed yet), 5xx and network failures: the service is not there to answer.
  if (!status || status === 404 || status >= 500) {
    return new PasswordResetError('unavailable', 'Password reset is not available right now.');
  }
  return new PasswordResetError('other', msg || 'Password reset failed.');
}

/**
 * Ask for a reset message. Resolves for any registered or unknown email alike (the API should not
 * reveal which addresses exist), and rejects with a PasswordResetError when the service is down.
 * @param email the account email
 * @param botId the agent the user came from, so the link and branding match
 */
export async function requestPasswordReset(email: string, botId?: string): Promise<void> {
  const resetUrl = `${window.location.origin}/reset-password${botId ? `?id=${encodeURIComponent(botId)}` : ''}`;
  try {
    await axios.post(
      `${API_BASE_URL}${PASSWORD_RESET_PATHS.request}`,
      { email, botId: botId || undefined, resetUrl },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
  } catch (error: any) {
    console.error('❌ [requestPasswordReset] failed:', error?.response?.status || error?.message);
    throw toPasswordResetError(error);
  }
}

/**
 * Set a new password with the token from the reset link.
 * Rejects with PasswordResetError code 'invalid' or 'expired' for a bad or old link.
 */
export async function completePasswordReset(token: string, password: string): Promise<void> {
  try {
    await axios.post(
      `${API_BASE_URL}${PASSWORD_RESET_PATHS.confirm}`,
      { token, password },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
  } catch (error: any) {
    console.error('❌ [completePasswordReset] failed:', error?.response?.status || error?.message);
    throw toPasswordResetError(error);
  }
}

/**
 * Email verification (account API, v2.08). The sign-up email links to
 * /verify-email?id=<bot>&token=<token>; the page POSTs the token only when the person presses its button,
 * because mail scanners (e.g. Outlook Safe Links) open links on their own and must not use up the token.
 *   200 {verified:true} (also when the link was already used) -> 'verified'
 *   410 -> 'expired' · 400 / 422 -> 'invalid' · 404, 5xx, no answer, anything else -> 'unavailable'
 */
export const EMAIL_VERIFY_PATH = '/email/verify'; // POST { token }, unauthenticated

export type VerifyEmailResult = 'verified' | 'expired' | 'invalid' | 'unavailable';

export async function verifyEmail(token: string): Promise<VerifyEmailResult> {
  try {
    await axios.post(
      `${API_BASE_URL}${EMAIL_VERIFY_PATH}`,
      { token },
      { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
    );
    return 'verified';
  } catch (error: any) {
    const status: number | undefined = error?.response?.status;
    console.error('❌ [verifyEmail] failed:', status || error?.message);
    if (status === 410) return 'expired';
    if (status === 400 || status === 422) return 'invalid';
    return 'unavailable';
  }
}

/**
 * Fetches user details using the user ID.
 * @param {string} userId - The ID of the user.
 * @param {string} token - Authorization token.
 * @returns {Promise<any>} - The user data.
 */
export async function getUserById(userId: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/users/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: {
        fields: [
          "id",
          "email",
          "username",
          "salutation",
          "firstname",
          "lastname",
          "org",
          "pic",
          "emailVerified",
          "verificationToken",
        ]
      },
    });
    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching user:", error.response?.data || error.message);
    throw new Error(error.response?.data?.error || "Failed to fetch user");
  }
}

/**
 * Load threads for a user with pagination
 * @param userId - The ID of the user
 * @param token - Authentication token
 * @param offset - Pagination offset (default: 0)
 * @param limit - Number of items per page (default: 5)
 * @returns Promise with thread data
 */
export async function loadThreads(userId: string, token: string, offset = 0, limit = 5): Promise<any> {
  const apiUrl = `${API_BASE_URL}/thread/`;

  if (!token) {
    console.error('❌ No token found');
    throw new Error('Authentication token not found.');
  }

  try {
    const filter = {
      where: { userId: userId },
      fields: [
        'id', 
        'threadId', 
        'created_at', 
        'metadata', 
        'tool_resources'
      ],
      offset,
      limit,
      order: ['created_at DESC'],
    };

    const encodedFilter = encodeURIComponent(JSON.stringify(filter));

    const response = await axios.get(`${apiUrl}?filter=${encodedFilter}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Format metadata.org if it exists
    const formattedData = response.data.map((thread: any) => {
      if (thread.metadata?.org && typeof thread.metadata.org === 'object') {
        thread.metadata.org = `${thread.metadata.org.orgName} (${thread.metadata.org.orgId})`;
      }
      return thread;
    });

    console.log('✅ - Get threads succeeded:', formattedData);
    return formattedData;
  } catch (error: any) {
    console.error('❌ [loadThreads] - Error fetching threads:', error.response?.data || error.message);
    throw new Error('Failed to fetch thread data.');
  }
}

// Get i Bots
export async function getiBots(token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/iBots`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    console.log('Get iBots succeeded: ', response.data);
    return response.data;
  } catch (error: any) {
    console.error("❌ Error fetching bots:", error.response?.data || error.message);
    throw new Error("Failed to fetch iBot data.");
  }
}

export interface HistoryItem {
  role: string;
  transcript: string;
  runId?: string;
  time?: string;
  message?: any;
}

export async function getChatHistorybyOrgUser(this: any, orgId: string, userId: string, token: string): Promise<HistoryItem[] | null> {
  const apiUrl = `${API_BASE_URL}/chatHistory`;

  if (!token) {
    console.error('No token found');
    throw new Error('Authentication token not found.');
  }

  try {
    console.log("UserId", userId);
    console.log("OrgId", orgId);

    // Filter by
    const assistantId = localStorage.getItem("selectedBot");
    console.log("✅ Current AssistantId:", assistantId);
    
    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: {
        filter: JSON.stringify({
          where: {
            userId: userId,            // must be exact string match
            assistantId: assistantId
          },
          fields: {
            id: true,
            userId: true,
            orgId: true,
            runId: true,
            threadId: true,
            assistantId: true,
            role: true,
            time: true,
            message: true,
          },
          limit: 100,
          order: ['time DESC']
        }),
      },
    });

    const chatHistory = [...response.data];
    console.log(`Chat History from Server: ${JSON.stringify(chatHistory)}`);

    // Flatten nested messages and log each index
    const HistoryItems: HistoryItem[] = [];

    for (const entry of chatHistory) {
      console.log('🔍 Processing entry:', entry);
    
      if (!Array.isArray(entry.message)) {
        console.warn('⚠️ Invalid entry.message — skipping:', entry);
        continue;
      }
    
      for (let groupIndex = 0; groupIndex < entry.message.length; groupIndex++) {
        const messageGroup = entry.message[groupIndex];
        console.log(`📦 Group ${groupIndex}:`, messageGroup);
    
        if (!Array.isArray(messageGroup)) {
          console.warn(`⚠️ Skipping non-array message group at index ${groupIndex}`);
          continue;
        }
    
        for (let msgIndex = 0; msgIndex < messageGroup.length; msgIndex++) {
          const msg = messageGroup[msgIndex];
          console.log(`🗂️ Message [${groupIndex}][${msgIndex}] =`, msg);
    
          if (msg && msg.role && msg.transcript) {
            HistoryItems.push({
              role: msg.role,
              transcript: msg.transcript,
              runId: entry.runId,
              time: entry.time,
              message: entry.message
            });
          } else {
            console.warn(`⚠️ Skipped invalid message at [${groupIndex}][${msgIndex}]`, msg);
          }
        }
      }
    }
    
    console.log('✅ Final History Items:', HistoryItems);
    return HistoryItems;

  } catch (error: any) {
    console.warn('⚠️ No existing chat history found for org/user. Proceeding.');
    return null;
  }
}

// Fetch chat history by runId (e.g., session id) for a specific assistant
export async function getChatHistoryByRunId(
  runId: string,
  token: string,
  assistantId?: string
): Promise<HistoryItem[] | null> {
  const apiUrl = `${API_BASE_URL}/chatHistory`;

  if (!token) {
    console.error('No token found');
    throw new Error('Authentication token not found.');
  }

  try {
    const where: Record<string, any> = { runId };
    if (assistantId) where.assistantId = assistantId;

    const filter = {
      where,
      fields: {
        id: true,
        userId: true,
        orgId: true,
        runId: true,
        threadId: true,
        assistantId: true,
        role: true,
        time: true,
        message: true,
      },
      order: ['time ASC'],
      limit: 100,
    };

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: { filter: JSON.stringify(filter) },
    });

    const chatHistory = [...response.data];
    console.log(`[ChatHistory] Loaded ${chatHistory.length} entries for runId=${runId}`);

    const HistoryItems: HistoryItem[] = [];

    for (const entry of chatHistory) {
      if (!Array.isArray(entry.message)) {
        console.warn('[ChatHistory] Invalid entry.message — skipping:', entry);
        continue;
      }

      for (const group of entry.message) {
        if (!Array.isArray(group)) continue;
        for (const msg of group) {
          if (msg && msg.role && msg.transcript) {
            HistoryItems.push({
              role: msg.role,
              transcript: msg.transcript,
              runId: entry.runId,
              time: entry.time,
              message: entry.message,
            });
          }
        }
      }
    }

    return HistoryItems.length ? HistoryItems : null;
  } catch (error: any) {
    console.warn('[ChatHistory] No history found for runId:', runId, error?.message);
    return null;
  }
}

// Fetch prior sessions for Memory Recall — last N days, max limit sessions,
// filtered by userId + assistantId. Excludes currentRunId entries post-fetch.
// Never throws — returns null on any failure so startup is never blocked.
export async function getMemoryRecallSessions(
  userId: string,
  assistantId: string,
  token: string,
  currentRunId?: string | null,
  days: number = 30,
  limit: number = 6
): Promise<HistoryItem[] | null> {
  const apiUrl = `${API_BASE_URL}/chatHistory`;

  if (!token || !userId || !assistantId) {
    console.warn('[MemoryRecall] Missing required params — skipping fetch.');
    return null;
  }

  try {
    const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

    const filter = {
      where: {
        userId,
        assistantId,
        time: { gte: cutoff },
      },
      fields: {
        id: true,
        userId: true,
        orgId: true,
        runId: true,
        threadId: true,
        assistantId: true,
        role: true,
        time: true,
        message: true,
      },
      order: ['time DESC'],
      limit,
    };

    const response = await axios.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: { filter: JSON.stringify(filter) },
    });

    const chatHistory = [...(response.data || [])];
    console.log(`[MemoryRecall] Loaded ${chatHistory.length} entries for userId=${userId} assistantId=${assistantId}`);

    const HistoryItems: HistoryItem[] = [];

    for (const entry of chatHistory) {
      // Exclude the current active session
      if (currentRunId && entry.runId === currentRunId) continue;

      if (!Array.isArray(entry.message)) continue;

      for (const group of entry.message) {
        if (!Array.isArray(group)) continue;
        for (const msg of group) {
          if (msg && msg.role && msg.transcript) {
            HistoryItems.push({
              role: msg.role,
              transcript: msg.transcript,
              runId: entry.runId,
              time: entry.time,
              message: entry.message,
            });
          }
        }
      }
    }

    return HistoryItems.length ? HistoryItems : null;
  } catch (error: any) {
    // Fallback: if server rejected the time filter, retry without it
    try {
      console.warn('[MemoryRecall] time filter rejected — retrying without date range:', error?.message);
      const fallbackFilter = {
        where: { userId, assistantId },
        fields: { id: true, userId: true, orgId: true, runId: true, threadId: true, assistantId: true, role: true, time: true, message: true },
        order: ['time DESC'],
        limit,
      };
      const fallbackResponse = await axios.get(apiUrl, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        params: { filter: JSON.stringify(fallbackFilter) },
      });
      const cutoffMs = Date.now() - days * 24 * 60 * 60 * 1000;
      const fallbackHistory = (fallbackResponse.data || []).filter(
        (e: any) => !e.time || new Date(e.time).getTime() >= cutoffMs
      );
      const FallbackItems: HistoryItem[] = [];
      for (const entry of fallbackHistory) {
        if (currentRunId && entry.runId === currentRunId) continue;
        if (!Array.isArray(entry.message)) continue;
        for (const group of entry.message) {
          if (!Array.isArray(group)) continue;
          for (const msg of group) {
            if (msg && msg.role && msg.transcript) {
              FallbackItems.push({ role: msg.role, transcript: msg.transcript, runId: entry.runId, time: entry.time, message: entry.message });
            }
          }
        }
      }
      return FallbackItems.length ? FallbackItems : null;
    } catch (fallbackError: any) {
      console.warn('[MemoryRecall] Fallback fetch also failed — skipping recall:', fallbackError?.message);
      return null;
    }
  }
}


/**
 * Create a new organization
 * @param orgData - Data for the new organization
 * @param token - Authorization token
 * @returns Promise with the API response
 */
export async function createOrg(orgData: {
  orgName: string;
  description?: string;
  website?: string;
  contact?: {
    name?: string;
    email?: string;
    phone?: string;
  };
  branding?: {
    logoUrl?: string;
    colorScheme?: string;
  };
  isActive?: boolean;
}, token: string): Promise<any> {
  try {
    const response = await axios.post(`${API_BASE_URL}/orgs`, orgData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ Create Org Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ Create Org failed.');
  }
}

/**
 * Get all organizations
 * @param token - Authorization token
 * @returns Promise with the list of organizations
 */
export async function getOrgs(token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/orgs`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ Get Orgs Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ Get Orgs failed.');
  }
}

/**
 * Get an organization by its ID
 * @param orgId - The ID of the organization
 * @param token - Authorization token
 * @returns Promise with the organization data
 */
export async function getOrgById(orgId: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/orgs/${orgId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ Get Org by ID Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ Get Org by ID failed.');
  }
}

/**
 * Get an image by its ID
 * @param Id - The ID of the imageStorage file
 * @param token - Authorization token
 * @returns Promise with the organization data
 */
export async function getImageById(id: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/imageStorage/download/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        
      },
      responseType: 'blob',
    });
    console.log('Returning Image: ', JSON.stringify(response.data));

    /*
    Change the axios GET request in getImageById to request binary data (responseType: 'blob' ).
    Convert the binary data to a URL object (e.g., using URL.createObjectURL) before returning or in the component.
    */
      const imageUrl = URL.createObjectURL(response.data);
      return { url: imageUrl };

  } catch (error: any) {
    console.error('❌ Get Image by id Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ Get Image by id failed.');
  }
}

/**
 * Create a new BotMetricClient record (Payload01)
 * @param metricData - Metric payload aligned with BotMetricClient model
 * @param token - Authorization token
 * @returns Promise with the API response
 */
export async function createBotMetricClient(
  metricData: {
    createdOn: string;
    botId: string;
    orgId: string;
    botName?: string;
    memberId?: string;
    userName: string;
    sessionId: string;
    clicksPerPrompt: number;
    promptClicks?: Array<{
      prompt: string;
      count: number;
      lastClicked?: string;
    }>;
    elementClicks?: Array<Record<string, any>>;
    uniqueQueries: number;
    questionsPerSession: number;
    engagementTime: string | number;
    endedOn?: string;
  },
  token: string
): Promise<any> {
  try {
    // Persist immediately so we don't lose the payload if the tab dies
    const normalized = normalizeMetricsPayload(metricData);
    const queuedId = enqueueMetric(normalized);

    // Try flushing any previous failures first with the current token
    await flushMetricQueue(token);

    // Try a blocking form POST first (closest to $.ajax async:false)
    const syncFormOk = postMetricsSyncForm(normalized, token);
    if (syncFormOk) {
      removeMetricFromQueue(queuedId);
      return { ok: true, syncForm: true };
    }

    // First attempt with keepalive so it survives tab close
    const keepaliveResult = await postMetricsWithKeepalive(normalized, token);
    if (keepaliveResult) {
      console.log("✅ BotMetricClient created successfully (beacon/keepalive)");
      removeMetricFromQueue(queuedId);
      return keepaliveResult;
    }

    // Last resort: synchronous blocking POST (deprecated but survives unload)
    const syncOk = postMetricsSync(normalized, token);
    if (syncOk) {
      removeMetricFromQueue(queuedId);
      return { ok: true, sync: true };
    }

    // Fallback to axios if keepalive is unavailable or failed
    const response = await axios.post(METRICS_URL, normalized, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    console.log("✅ BotMetricClient created successfully (axios fallback):", response.data);
    removeMetricFromQueue(queuedId);
    return response.data;
  } catch (error: any) {
    console.error('❌ Create BotMetricClient Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ Create BotMetricClient failed.');
  }
}

/**
 * Fire-and-forget attempt to send metrics synchronously (for unload handlers).
 * Returns true if any synchronous path succeeded.
 */
export function sendMetricsImmediate(metricData: Record<string, any>, token: string): boolean {
  // console.log('[Metrics] sendMetricsImmediate start', { hasToken: !!token, url: METRICS_URL });
  const payload = normalizeMetricsPayload(metricData);
  // Try beacon
  const body = JSON.stringify(payload);
  const beaconUrl = getMetricsUrl(true, token);
  let beaconSent = false;
  try {
    // console.log('[Metrics] Attempt sendBeacon', { url: beaconUrl });
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const sent = navigator.sendBeacon(beaconUrl, new Blob([body], { type: 'application/json' }));
      if (sent) {
        // console.log('[Metrics] sendBeacon success');
        beaconSent = true;
      }
      // console.warn('[Metrics] sendBeacon returned false');
    }
  } catch (err) {
    // console.warn('[Metrics] Beacon failed:', err);
  }

  // Try synchronous form post
  const formOk = postMetricsSyncForm(payload, token);
  // console.log('[Metrics] Sync form post result', formOk);
  if (formOk) return true;

  // Try synchronous JSON post
  const jsonOk = postMetricsSync(payload, token);
  // console.log('[Metrics] Sync JSON post result', jsonOk);
  if (jsonOk) return true;

  // As a last resort, kick off a keepalive fetch (non-blocking)
  // console.log('[Metrics] Falling back to keepalive fetch');
  postMetricsWithKeepalive(payload, token);
  return beaconSent;
}

/**
 * PRIMARY metrics transport: a keepalive fetch with the token in the Authorization
 * header and the default (non-credentialed) cross-origin credentials mode.
 *
 * `keepalive: true` lets this single request complete BOTH while the page is alive
 * (tab hidden/backgrounded) AND through a hard tab close (it survives unload) — so it
 * replaces sendBeacon as the unload transport on modern browsers. It avoids both of
 * sendBeacon's fatal quirks for this use case: beacons can't set headers (which forced
 * the token into `?auth=`) and are always credentialed (which tripped the CORS
 * "no wildcard origin with credentials" rule). sendBeacon now survives only as a
 * fallback in sendMetricsImmediate for browsers without keepalive-fetch (iOS Safari < 16.4).
 */
export async function sendMetricsAsync(metricData: Record<string, any>, token: string) {
  // Mobile safety: on browsers without fetch-keepalive (iOS Safari < 16.4) this request would
  // NOT survive page unload/freeze, and awaiting it could block the synchronous sendBeacon
  // fallback from firing before the page is frozen. Bail fast so the finalize chain reaches
  // sendMetricsImmediate() (sendBeacon) while the page is still alive.
  if (!SUPPORTS_FETCH_KEEPALIVE) return false;
  try {
    const payload = normalizeMetricsPayload(metricData);
    const res = await fetch(METRICS_URL, {
      method: 'POST',
      keepalive: true,   // survive page unload — replaces sendBeacon on modern browsers
      mode: 'cors',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch (err) {
    return false;
  }
}
const ASSISTANTS_URL = `${API_BASE_URL}/assistants`;
const ASSISTANT_VERSION_URL = `${API_BASE_URL}/assistants/version`;
const ASSISTANT_CONFIG_URL = `${API_BASE_URL}/assistants`;
const ASSISTANT_SETTINGS_URL = `${API_BASE_URL}/assistants`;


export async function getAssistantVersion(params: {
  org: any;
  botId: string;
  name: string;
  type: string;
  version: number;
}, token: string): Promise<any> {
  try {
    console.log('[API] getAssistantVersion request', {
      version: params.version,
      botId: params.botId,
      name: params.name,
      type: params.type,
      hasOrg: !!params.org,
    });
    const response = await axios.get(`${API_BASE_URL}/assistants/versions/${params.version}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      params: {
        name: params.name,
        type: params.type,
        org: params.org,
        botId: params.botId,
      },
    });
    console.log('[API] getAssistantVersion success', {
      version: params.version,
      keys: Object.keys(response.data || {}),
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ getAssistantVersion Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ getAssistantVersion failed.');
  }
}

export async function getAssistant(id: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${ASSISTANTS_URL}/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ getAssistant Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ getAssistant failed.');
  }
}

export async function getAssistantConfig(id: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${ASSISTANT_CONFIG_URL}/${id}/config`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ getAssistantConfig Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ getAssistantConfig failed.');
  }
}

export async function getAssistantSettings(id: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${ASSISTANT_SETTINGS_URL}/${id}/setting`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ getAssistantSettings Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ getAssistantSettings failed.');
  }
}

// [S2-A] BotSettings read — twin of relay-server/lib/botfactoryapi.js:500. GET /botsettings/{id}
// resolves agentId → legacyAssistantId → ObjectId server-side (botsettings.controller.ts:178-198),
// so callers pass whichever id they hold. Class-level @authenticate('jwt'); a freshly minted GUEST
// JWT gets 200 (probed on botapi-v2 2026-09-18). This is the home the relay reads orgId from
// (bot-config.js:661) and therefore the guest attribution source of truth (S2 note §2.3, Grok Note 1).
// Throws a real Error with statusCode preserved so the caller can tell fetch-fail from absent-org.
export async function getBotSettings(id: string, token: string): Promise<any> {
  try {
    const response = await axios.get(`${API_BASE_URL}/botsettings/${id}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    const apiErr = error.response?.data?.error;
    const message =
      (apiErr && typeof apiErr === 'object' && (apiErr.message || apiErr.code)) ||
      (typeof apiErr === 'string' ? apiErr : null) ||
      error.message ||
      '❌ getBotSettings failed.';
    const wrapped: any = new Error(message);
    wrapped.statusCode = apiErr?.statusCode ?? error.response?.status;
    wrapped.code = apiErr?.code;
    throw wrapped;
  }
}

export async function patchAssistant(id: string, data: Record<string, any>, token: string): Promise<any> {
  try {
    const response = await axios.patch(`${ASSISTANTS_URL}/${id}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ patchAssistant Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ patchAssistant failed.');
  }
}

export async function putAssistant(id: string, data: Record<string, any>, token: string): Promise<any> {
  try {
    const response = await axios.put(`${ASSISTANTS_URL}/${id}`, data, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ putAssistant Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ putAssistant failed.');
  }
}

export async function postAssistantVersion(payload: Record<string, any>, token: string): Promise<any> {
  try {
    const response = await axios.post(`${ASSISTANT_VERSION_URL}`, payload, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  } catch (error: any) {
    console.error('❌ postAssistantVersion Error:', error.response?.data || error.message);
    throw new Error(error.response?.data?.error || '❌ postAssistantVersion failed.');
  }
}
